import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { Text, Dropdown, Button as WCButton } from '@cuemath/web-comps';
import { COLORS, FlexView } from '@cuemath/comps';
import { TextArea } from 'semantic-ui-react';

import { postStudentTransferStatus } from 'actions/student-transfer/single-student';
import { openModal, closeModal } from 'actions/modal';
import {
  getStudentTransferStatus,
  postStartTransferProcess,
} from 'actions/student-transfer/single-student';
import {
  STUDENT_TRANSFER_REASONS_OPTIONS,
  TRANSFER_REASONS,
  stCreatedByDummyId,
} from 'constants/student-transfer';

import { ReactComponent as LoaderSVG } from 'img/loader.svg';

import { getStudentFirstName, getRestAPIGetErrorMessage, getCookie } from 'utils';
import {
  getSingleStudentTransferPostData,
  singleStudentTransferButtonDisable,
  reasonBasedWarningMessage,
} from 'utils/student-transfer';

import TransferSlotStatus from './slot-status';
import SlotDayTime from './slot-day-time';
import PreferredTeacherSelected from './reason-based-process/preferred-teacher';

const topToBottom = keyframes`
  0% {
    top: -50px;
  }
  100% {
    top:0;
  }
`;

const bottomToTop = keyframes`
  0% {
    top: 0;
  }

  100% {
    top: -50px;
    display: none;
  }
`;

const Wrapper = styled(FlexView)`
  gap: 24px;
  height: ${({ height }) => height};
`;

const ErrorMessage = styled(Text)`
  width: 100%;
  padding: 4px 6px;
  border-radius: 8px;
  background-color: ${COLORS.RED_SHADE};
  position: relative;
  top: -50px;
  z-index: -1;

  animation-name: ${topToBottom}, ${bottomToTop};
  animation-iteration-count: 1;
  animation-duration: 300ms;
  animation-delay: 0s, 60s;
  animation-timing-function: ease-out;
  animation-fill-mode: forwards;
`;

const SelectionCard = styled(FlexView)`
  width: ${({ width }) => width};
  gap: ${({ gap }) => gap || 8}px;
  padding: 0;
`;

const DropdownWrapper = styled.div`
  width: 320px;
`;

const DropdownWarningMessage = styled(Text)`
  padding: 0 16px;
`;

const Button = styled(WCButton)`
  width: fit-content;
  padding: 16px 24px;

  & > div {
    text-align: center;
  }
`;

const LoaderIcon = styled(LoaderSVG)`
  width: 70px;
  height: 70px;

  path {
    stroke: ${COLORS.COLA};
  }
`;

const StyledTextArea = styled(TextArea)`
  width: 500px;
  padding: 8px;
  border: none;
  outline: 1px solid ${COLORS.GRAY_XLIGHT};
  border-radius: 8px;
  text-transfor &:focus {
    outline: 1px solid ${COLORS.COLA};
  }
  ::placeholder {
    font-style: italic;
  }
`;

const initState = [
  {
    day: '',
    time: '',
    id: `student-transfer-slots-${1}`,
  },
];

const StudentTransferDetails = ({ studentDetail }) => {
  const { first_name: firstName, name: fullName, student_id: studentId } =
    studentDetail || {};

  const studentName = getStudentFirstName({ firstName, fullName });

  const [transferReason, setTransferReason] = useState('');
  const [slotsList, setSlotsList] = useState(initState);
  const [transferDetails, setTransferDetails] = useState(null);
  const [assignFailErrorMessage, setAssignFailErrorMessage] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [preferredTeacherData, setPreferredTeacherData] = useState({
    courseSelected: '',
    preferredTeacherId: '',
  });
  const [isDirectMappingTransfer, setDirectMappingTransfer] = useState(false);
  const [detailedReason, setDetailedReason] = useState('');
  const [isRetransferReasonRequired, setIsRetransferReasonRequired] = useState(false);

  const onStudentTransferCompletion = (errorMessage, data) => {
    if (errorMessage) {
      setAssignFailErrorMessage(errorMessage);
    } else {
      setTransferDetails(data);
      setShowForm(false);
      setAssignFailErrorMessage('');
      setIsRetransferReasonRequired(data?.is_retransfer_reason_required ?? false);
      setDetailedReason('');
    }
  };

  const dispatch = useDispatch();
  const { post: initiateStudentTransfer, isProcessing } = postStudentTransferStatus({
    onComplete: onStudentTransferCompletion,
  });

  const {
    post: initiateStartTransferProcess,
    isProcessing: isStartingProcess,
  } = postStartTransferProcess({
    onComplete: onStudentTransferCompletion,
  });

  const {
    get: getStudentTransfer,
    data: studentTransferData,
    resource: studentTransferGetAPIResource,
    isProcessingFailed: studentTransferGetFail,
    isProcessing: studentTransferGetProcessing,
  } = getStudentTransferStatus();

  const studentTransferGetAPIErrorMessage =
    studentTransferGetFail && getRestAPIGetErrorMessage(studentTransferGetAPIResource);

  const showTransferDetails = !showForm && transferDetails;

  const addMoreSlots = () => {
    setSlotsList(prev => [
      ...prev,
      {
        day: '',
        time: '',
        id: `student-transfer-slots-${prev.length + 1}`,
      },
    ]);
  };

  const updateSlotsList = useCallback(updateSlotData => {
    setSlotsList(prev =>
      prev.map(slot => {
        if (slot.id === updateSlotData.id) {
          return updateSlotData;
        }
        return slot;
      }),
    );
  }, []);

  const onReasonSelection = transferReason => {
    setTransferReason(transferReason);

    if (transferReason !== TRANSFER_REASONS['CLASS_SLOT_ISSUE']) {
      setSlotsList(initState);
    }
  };

  const findAndAssignMathTeacher = (assignTeacherViaDirectMapping = false) => {
    const finalData = getSingleStudentTransferPostData({
      transferReason: transferReason || TRANSFER_REASONS[transferDetails?.reason],
      slotsList,
      preferredTeacherData,
    });

    if (detailedReason) {
      finalData.detailed_reason = detailedReason;
    }
    if (assignTeacherViaDirectMapping) {
      finalData.assign_teacher_via_direct_mapping = true;
    }
    setDirectMappingTransfer(assignTeacherViaDirectMapping);

    initiateStudentTransfer(finalData, studentId);
  };

  const startTransferProcess = () => {
    const userId = getCookie('user_id') || getCookie('emp_id') || stCreatedByDummyId;
    // Phase 1: frees the old teacher's slots and marks the course in-progress.
    // The admin assigns the new teacher afterwards via the reason + assign flow.
    // reason is omitted so the teacher service defaults it to ADMIN_DIRECT_TRANSFER.
    initiateStartTransferProcess({ created_by_id: userId }, studentId);
  };

  const handleOpenModal = modalData => {
    dispatch(
      openModal(
        'student-transfer-load-screen',
        {
          blocking: true,
          transparent: false,
          size: 'custom',
          width: '512px',
          height: '560px',
          radius: '16px',
          modalZIndex: 1000,
          backdropFilter: true,
          backdropFilterData: {
            blurValue: 'blur(20px)',
            bgColor: COLORS.COLA_LIGHTER,
          },
          closeIconAttributes: { placeCloseIcon: true },
        },
        modalData,
      ),
    );
  };

  useEffect(() => {
    if (isProcessing) {
      handleOpenModal({
        type: 'loading',
        studentName,
        assignTeacherViaDirectMapping: isDirectMappingTransfer,
      });
    } else if (assignFailErrorMessage) {
      handleOpenModal({
        type: 'loadingFailed',
        onRetry: () => findAndAssignMathTeacher(),
        assignFailErrorMessage,
      });
    } else {
      dispatch(closeModal());

      setShowLoader(false);
      setShowForm(false);

      if (studentTransferData && !transferDetails) {
        setTransferDetails(studentTransferData);
        setIsRetransferReasonRequired(
          studentTransferData?.is_retransfer_reason_required ?? false,
        );
        setPreferredTeacherData(prev => ({
          ...prev,
          courseSelected: studentTransferData?.course_stream,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing, assignFailErrorMessage, studentTransferGetProcessing]);

  useEffect(() => {
    setShowLoader(true);
    getStudentTransfer(studentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preferredTeacherReasonSelected =
    transferReason === TRANSFER_REASONS['PREFERRED_TEACHER'] ||
    transferDetails?.reason === 'PREFERRED_TEACHER';
  const showSlotsData =
    transferReason === TRANSFER_REASONS['CLASS_SLOT_ISSUE'] ||
    preferredTeacherReasonSelected;

  const warningMessage = transferReason && reasonBasedWarningMessage(transferReason);
  const isSlotPending =
    transferDetails?.status === 'INITIATED' || transferDetails?.status === 'SLOT_PENDING';

  // Two-phase admin direct transfer sitting in-process (no teacher assigned yet).
  // Here the admin must supply the target teacher, so gate the assign button on a
  // preferred_teacher_id in addition to the slots.
  const isAdminTransferInProcess =
    transferDetails?.reason === 'ADMIN_DIRECT_TRANSFER' &&
    transferDetails?.status === 'INITIATED' &&
    !transferDetails?.teacher_name;

  const disableAssignButton = isAdminTransferInProcess
    ? !preferredTeacherData?.preferredTeacherId ||
      singleStudentTransferButtonDisable({
        transferReason: TRANSFER_REASONS['CLASS_SLOT_ISSUE'],
        slotsList,
        preferredTeacherData,
      })
    : singleStudentTransferButtonDisable({
        transferReason: isSlotPending
          ? TRANSFER_REASONS['CLASS_SLOT_ISSUE']
          : transferReason,
        slotsList,
        preferredTeacherData,
      });

  if (showLoader) {
    return (
      <Wrapper height="100%" alignItems="center" justifyContent="center">
        <LoaderIcon />
      </Wrapper>
    );
  }

  const crmProductId = transferDetails?.crm_product_id;

  const canShowAdmissionToolLink =
    ['TRIAL_SLOT_PENDING', 'DIRECT_MAPPING_REQUESTED'].includes(
      transferDetails?.status,
    ) && crmProductId;

  const enableDirectMappingTransfer =
    transferReason !== TRANSFER_REASONS['PREFERRED_TEACHER'] &&
    transferReason !== TRANSFER_REASONS['CLASS_SLOT_ISSUE'];

  if (showTransferDetails) {
    return (
      <Wrapper>
        {transferDetails?.message && (
          <ErrorMessage variant="body1Medium" color={COLORS.WHITE}>
            {transferDetails?.message || '-'}
          </ErrorMessage>
        )}
        <TransferSlotStatus
          studentName={studentName}
          transferDetails={transferDetails}
          transferReason={transferReason || TRANSFER_REASONS[transferDetails?.reason]}
          preferredTeacherData={preferredTeacherData}
          setPreferredTeacherData={setPreferredTeacherData}
          preferredTeacherReasonSelected={preferredTeacherReasonSelected}
        />
        {isSlotPending && (
          <SelectionCard width="max-content">
            <SlotDayTime
              slotsList={slotsList}
              updateSlotsList={updateSlotsList}
              addMoreSlots={addMoreSlots}
              setSlotsList={setSlotsList}
              initState={initState}
            />
          </SelectionCard>
        )}
        <Button
          label={
            isSlotPending
              ? `Create slot and assign teacher.`
              : `Reassign math teacher for ${studentName}`
          }
          isDisabled={isSlotPending && disableAssignButton}
          onClick={
            isSlotPending
              ? () => findAndAssignMathTeacher()
              : () => {
                  setDetailedReason('');
                  setTransferDetails(null);
                  setDirectMappingTransfer(false);
                }
          }
          isBusy={isProcessing}
        />
        {canShowAdmissionToolLink && (
          <div style={{ marginTop: 16 }}>
            <a
              href={`https://leap-admin.cuemath.com/admission-tool/${crmProductId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: COLORS.COLA,
                fontWeight: 600,
                textDecoration: 'underline',
                fontSize: 16,
              }}
            >
              Open Admission Tool for this Student
            </a>
          </div>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper alignItems="flex-start">
      {studentTransferGetFail && (
        <ErrorMessage variant="body1Medium" color={COLORS.WHITE}>
          {studentTransferGetAPIErrorMessage}
        </ErrorMessage>
      )}
      <SelectionCard alignItems="flex-start" justifyContent="center">
        <Text variant="h6">Start the transfer process for {studentName}</Text>
        <Text variant="body3" color={COLORS.COLA_LIGHTER}>
          Frees {studentName}’s current teacher and pauses lapsing while you find
          a new teacher. Assign the new teacher below once the process has started.
        </Text>
        <Button
          label="Start transfer process"
          onClick={startTransferProcess}
          isBusy={isStartingProcess}
        />
      </SelectionCard>
      <SelectionCard alignItems="flex-start" justifyContent="center">
        <Text variant="h6">Pick a reason for {studentName}’s transfer</Text>
        <DropdownWrapper>
          <Dropdown
            options={STUDENT_TRANSFER_REASONS_OPTIONS}
            width="320px"
            height="max-content"
            placeholder="Reason for transfer"
            type="primary"
            onChange={value => onReasonSelection(value)}
            value={transferReason}
          />
          <DropdownWarningMessage variant="body3">
            {warningMessage}
          </DropdownWarningMessage>
        </DropdownWrapper>
      </SelectionCard>

      {preferredTeacherReasonSelected && (
        <PreferredTeacherSelected
          preferredTeacherData={preferredTeacherData}
          setPreferredTeacherData={setPreferredTeacherData}
        />
      )}

      {showSlotsData && (
        <SelectionCard>
          <SlotDayTime
            slotsList={slotsList}
            updateSlotsList={updateSlotsList}
            addMoreSlots={addMoreSlots}
            setSlotsList={setSlotsList}
            initState={initState}
            title={
              preferredTeacherReasonSelected &&
              'Edit/Add regular slots (Optional if slots available)'
            }
          />
        </SelectionCard>
      )}

      {isRetransferReasonRequired && (
        <>
          <Text variant="h6">Provide a detailed reason for the re-transfer</Text>
          <StyledTextArea
            rows={3}
            placeholder="Please provide a detailed reason for the re-transfer, including relevant chat links or screenshots for reference."
            onChange={(e, { value }) => {
              const trimmedValue = value?.trim();
              setDetailedReason(trimmedValue);
            }}
          />
        </>
      )}

      {transferReason && (
        <>
          <Button
            label={`Find and assign math teacher for ${studentName}`}
            isDisabled={
              disableAssignButton || (isRetransferReasonRequired && !detailedReason)
            }
            onClick={() => findAndAssignMathTeacher()}
            isBusy={isProcessing}
          />
          {enableDirectMappingTransfer && (
            <Button
              label="Assign Tutor via admission tool"
              onClick={() => findAndAssignMathTeacher(true)}
              style={{ marginTop: 12 }}
              isBusy={isProcessing}
            />
          )}
        </>
      )}
    </Wrapper>
  );
};

export default StudentTransferDetails;
